import React, { useState, useEffect, useRef } from 'react';
import { IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonBadge, IonText, IonSpinner } from '@ionic/react';
import { Card, CreatureCard, ArtCard, ManaSourceCard, CommanderCard } from './types';
import { getCardImageUrl } from './cardImageUtils';
import './CardDisplay.css';

interface CardDisplayProps {
  card: Card;
  size?: 'small' | 'medium' | 'large';
  onClick?: () => void;
  selected?: boolean;
  disabled?: boolean;
  showDetails?: boolean;
  lazy?: boolean; // Enable lazy loading (default true)
  isToken?: boolean; // Display as square token instead of card
}

export const CardDisplay: React.FC<CardDisplayProps> = React.memo(({
  card,
  size = 'medium',
  onClick,
  selected = false,
  disabled = false,
  showDetails = true,
  lazy = true,
  isToken = false,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(!lazy);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use card's imageUrl if available, otherwise generate from name
  const imageUrl = card.imageUrl || getCardImageUrl(card.name);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy || shouldLoad) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '100px', // Start loading 100px before visible
        threshold: 0.01,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [lazy, shouldLoad]);

  const getSizeClass = () => {
    switch (size) {
      case 'small': return 'card-display-small';
      case 'large': return 'card-display-large';
      default: return 'card-display-medium';
    }
  };

  const isCreature = (c: Card): c is CreatureCard => 'power' in c && 'health' in c;
  const isCommander = (c: Card): c is CommanderCard => 'health' in c && 'abilities' in c;

  return (
    <div
      ref={containerRef}
      className={`card-display ${getSizeClass()} ${isToken ? 'token' : ''} ${selected ? 'selected' : ''} ${disabled ? 'disabled' : ''} ${onClick ? 'clickable' : ''}`}
      onClick={onClick && !disabled ? onClick : undefined}
    >
      <div className="card-image-container">
        {!shouldLoad ? (
          <div className="card-loading">
            <IonSpinner />
          </div>
        ) : (
          <>
            {!imageLoaded && !imageError && (
              <div className="card-loading">
                <IonSpinner />
              </div>
            )}
            {imageError ? (
              <div className="card-image-fallback">
                <IonText>
                  <h3>{card.name}</h3>
                  {isCreature(card) && (
                    <p className="creature-stats">
                      {card.power}/{card.health}
                    </p>
                  )}
                </IonText>
              </div>
            ) : (
              <img
                src={imageUrl}
                alt={card.name}
                className={`card-image ${imageLoaded ? 'loaded' : ''}`}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
                loading="lazy"
              />
            )}
          </>
        )}
      </div>

      {showDetails && size !== 'small' && (
        <div className="card-details-overlay">
          <div className="card-name">{card.name}</div>
          {isCreature(card) && !isCommander(card) && (
            <div className="card-stats">
              <IonBadge color="primary">{card.power}/{card.health}</IonBadge>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// Display name for debugging
CardDisplay.displayName = 'CardDisplay';

export default CardDisplay;
